import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindActionCreators } from 'redux';
import {
  Card,
  CardBody,
  CardTitle,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { Loading } from '../../../../../loadbar/Loading';
import {
  clearRunupInfo as clearRunupInfoAc,
  getRunupInfo as getRunupInfoAc,
  getRunupReferences as getRunupReferencesAc,
  sortReferences as sortReferencesAc,
  toggleColumns as toggleColumnsAc,
} from '../../../../../../redux/tsunamis/runups/info/runupInfoActions';
import { parseAndShortenFloat } from '../../../../../../helperFunctions/helperFunctions';
import { SingleResultGrid } from '../../../../../grid/SingleResultGrid';
import { Column } from '../../../../../grid/Column';
import { ReferencesTable } from '../../../../../table/ReferencesTable';
import { CommentsSection } from '../../../../../form/CommentsSection';
import { EditIcon } from '../../../../../icon/EditIcon';
import { isDataManager } from '../../../../../../role/roleUtils';
import { TitleTooltip } from '../../../../../form/TitleToolTip';

const renderEditLink = id => (
  <Link aria-label={`Edit Runup ${id}`} to={`/view/hazards/tsunami/update-runup/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

const renderEditEventLink = id => (
  <Link aria-label={`Edit Tsunami Event ${id}`} to={`/view/hazards/tsunami/update-tsunami/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

class MoreRunupInfoContainerUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
  }

  componentDidMount() {
    document.title = 'NCEI Hazard Runup Information';
    const {
      match: { params }, getRunupReferences, getRunupInfo,
    } = this.props;
    getRunupReferences({ id: params.id });
    getRunupInfo({ id: params.id });
  }

  componentWillUnmount() {
    const { clearRunupInfo } = this.props;
    clearRunupInfo();
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortReferences, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortReferences(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      // eslint-disable-next-line no-unused-vars
      fetched, runup, references, sort, visibleColumns, match: { params: { id } }, authorities,
    } = this.props;

    const dataManager = isDataManager(authorities);

    if (!fetched) {
      return (<Loading />);
    }


    return (
      <div className="haz-results-container haz-page-container">
        <Card className="haz-page-card">
          <CardBody>
            <CardTitle tag="h1" className="haz-page-card-title">Runup Information</CardTitle>
            {dataManager && renderEditLink(id)}

            <div className="haz-info-content">
              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Tsunami Event</CardTitle>
                    <SingleResultGrid data={runup}>
                      {dataManager && (
                      <Column id="edit" cell={({ row }) => renderEditEventLink(row.get('tsunamiEventId'))} width={30}>
                        <EditIcon />
                      </Column>
                      )}
                      <Column id="year" width={75} helpModalId="year">Year</Column>
                      <Column id="month" width={50} helpModalId="month">Mo</Column>
                      <Column id="day" width={50} helpModalId="day">Dy</Column>
                      <Column id="hour" width={50} helpModalId="hour">Hr</Column>
                      <Column id="minute" width={50} helpModalId="minute">Mn</Column>
                      <Column id="second" width={75} helpModalId="second">Sec</Column>
                      <Column id="sourceEventValidity" width={85} helpModalId="tsuValidity">Tsunami Event Validity</Column>
                      <Column id="sourceCauseCode" width={85} helpModalId="tsuCauseCode">Tsunami Cause Code</Column>
                      <Column id="sourceOceanicTsunami" width={85} helpModalId="tsuOceanicTsunami" cell={({ value }) => (value ? 'Y' : 'N')}>Oceanic Tsunami</Column>
                      <Column id="sourceCountry" width={150} helpModalId="tsuCountry">Tsunami Country</Column>
                      <Column id="sourceLocationName" width={200} helpModalId="tsuLocationName">Location Name</Column>
                      <Column id="sourceLatitude" width={100} helpModalId="tsuLatitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Latitude</Column>
                      <Column id="sourceLongitude" width={100} helpModalId="tsuLongitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Longitude</Column>
                      <Column id="sourceMaxWaterHeight" helpModalId="tsuMaxWaterHeight" width={125}>Maximum Water Height (m)</Column>
                      <Column
                        id="sourceNumRunups"
                        width={100}
                        helpModalId="tsuRunups"
                        sortable
                        cell={({ value, row }) => (value ? <Link target="_blank" rel="noopener noreferrer" to={{ pathname: '/view/hazards/tsunami/runup-data', search: `?tsunamiEventId=${row.get('tsunamiEventId')}` }}>{value}</Link> : value)}
                      >
                        Number of Runups
                      </Column>
                      <Column id="sourceNumDeposits" width={100} helpModalId="tsuDeposits">Number of Deposits</Column>
                      <Column id="sourceTsMtAbe" width={100} helpModalId="tsuMagAbe">Tsunami Magnitude (Abe)</Column>
                      <Column id="sourceTsMtIi" width={100} helpModalId="tsuMagIida">Tsunami Magnitude (Iida)</Column>
                      <Column id="sourceTsIntensity" width={100} helpModalId="tsuIntensity">Tsunami Intensity</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Tsunami Runup</CardTitle>
                    <SingleResultGrid data={runup}>
                      <Column id="doubtful" width={100} helpModalId="ruDoubtful">Doubtful Runup</Column>
                      <Column id="country" width={150} helpModalId="ruCountry">Country</Column>
                      <Column id="area" width={200} helpModalId="ruArea">Area</Column>
                      <Column id="locationName" width={200} helpModalId="ruLocation">Location Name</Column>
                      <Column id="latitude" width={100} helpModalId="ruLatitude" cell={({ value }) => (<div>{parseAndShortenFloat(value, 5)}</div>)}>Latitude</Column>
                      <Column id="longitude" width={100} helpModalId="ruLongitude" cell={({ value }) => (<div>{parseAndShortenFloat(value, 5)}</div>)}>Longitude</Column>
                      <Column id="distFromSource" width={150} helpModalId="ruDistance" cell={({ value }) => (<div>{parseAndShortenFloat(value, 0)}</div>)}>Distance From Source (km)</Column>
                      <Column id="travHours" width={100} helpModalId="ruWaveTravelHour">Travel Hours</Column>
                      <Column id="travMins" width={100} helpModalId="ruWaveTravelMinute">Travel Minutes</Column>
                      <Column id="runupHt" width={150} helpModalId="ruHeight">Maximum Water Height (m)</Column>
                      {dataManager && (
                      <Column id="maxTroughHt" width={150} helpModalId="ruTroughHt">Maximum Trough Height (m)</Column>
                      )}
                      <Column id="runupHoriz" width={150} helpModalId="ruInundation">Max Inundation Distance (m)</Column>
                      <Column id="typeMeasurementId" width={125} helpModalId="ruMeasurementType">Measurement Type</Column>
                      <Column id="period" width={100} helpModalId="ruPeriod">Period</Column>
                      <Column id="firstMotion" width={85} helpModalId="ruFirstMotion">First Motion</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>


              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Tsunami Runup Location Effects</CardTitle>
                    <SingleResultGrid data={runup}>
                      <Column id="deaths" width={100} helpModalId="ruDeaths">Deaths</Column>
                      <Column id="deathsAmountOrder" width={125} helpModalId="ruDeathsDescription">Death Description</Column>
                      <Column id="missing" width={100} helpModalId="ruMissing">Missing</Column>
                      <Column id="missingAmountOrder" width={125} helpModalId="ruMissingDescription">Missing Description</Column>
                      <Column id="injuries" width={100} helpModalId="ruInjuries">Injuries</Column>
                      <Column id="injuriesAmountOrder" width={125} helpModalId="ruInjuriesDescription">Injuries Description</Column>
                      <Column id="damageMillionsDollars" width={100} helpModalId="ruDamage">Damage $Mil</Column>
                      <Column id="damageAmountOrder" width={125} helpModalId="ruDamageDescription">Damage Description</Column>
                      <Column id="housesDestroyed" width={100} helpModalId="ruHousesDestroyed">Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrder" width={125} helpModalId="ruHousesDestroyedDescription">Houses Destroyed Description</Column>
                      <Column id="housesDamaged" width={100} helpModalId="ruHousesDamaged">Houses Damaged</Column>
                      <Column id="housesDamagedAmountOrder" width={125} helpModalId="ruHousesDamagedDescription">Houses Damaged Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">
                      <TitleTooltip label="Comments" helpModalId="commentDisclaimer" />
                    </CardTitle>
                    <CommentsSection title="">{runup.get('comments')}</CommentsSection>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section haz-reference-info">
                <Card>
                  <CardBody>
                    <CardTitle>References for the Runup</CardTitle>
                    <ReferencesTable
                      references={references}
                      handleToggleColumns={this.handleToggleColumns}
                      sort={sort}
                      visibleColumns={visibleColumns}
                    />
                  </CardBody>
                </Card>
              </div>

            </div>


          </CardBody>
        </Card>
      </div>
    );
  }
}


MoreRunupInfoContainerUnwrapped.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ id: PropTypes.string }) }).isRequired,
  fetched: PropTypes.bool.isRequired,
  clearRunupInfo: PropTypes.func.isRequired,
  getRunupInfo: PropTypes.func.isRequired,
  getRunupReferences: PropTypes.func.isRequired,
  runup: ImmutablePropTypes.map.isRequired,
  references: ImmutablePropTypes.list.isRequired,
  sortReferences: PropTypes.func.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  authorities: ImmutablePropTypes.list.isRequired,
};

const mapStateToProps = (state) => {
  const fetchedRunup = state.getIn(['runupInfoPage', 'fetchedRunup']);
  const fetchedReferences = state.getIn(['runupInfoPage', 'fetchedReferences']);
  return {
    fetched: [fetchedRunup, fetchedReferences].every(v => v),
    runup: state.getIn(['runupInfoPage', 'runup']),
    references: state.getIn(['runupInfoPage', 'references']),
    sort: state.getIn(['runupInfoPage', 'sort']),
    visibleColumns: state.getIn(['runupInfoPage', 'visibleColumns']),
    sortReferences: PropTypes.func.isRequired,
    toggleColumns: PropTypes.func.isRequired,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  clearRunupInfo: clearRunupInfoAc,
  getRunupInfo: getRunupInfoAc,
  getRunupReferences: getRunupReferencesAc,
  sortReferences: sortReferencesAc,
  toggleColumns: toggleColumnsAc,
}, dispatch);

export const RunupInfoPage = connect(mapStateToProps, mapDispatchToProps)(MoreRunupInfoContainerUnwrapped);
